import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import { Box, Card, CardContent, CardMedia, Chip, Container, Grid, Paper, Stack } from '@mui/material';

import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { capitalizeFirstLetter } from '../utils/StringUtils';
import Litter from '../data/Litter';


const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: 'rotate(90deg)',
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)',
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

interface Props {
  litter: Litter;
  name: string;
  defaultExpanded: boolean;
}

function KittenFamily({ name, litter, defaultExpanded }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleNavigate = useCallback((name: string) => {
    navigate("/contact", { state: { subject: name }});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={name + "-content"}
        id={name + "-header"}
      >
        <Typography component="span">{t("litter")} {capitalizeFirstLetter(name)}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Paper elevation={0} square>
          <Container sx={{ xs: { py: 0, m: 0 }, md: { py: 4 } }}>
            <Grid container spacing={4} sx={{ xs: { m: 0, p: 0 }, md: { mt: 2, px: 5 } }} columns={6} justifyContent="center" alignItems="center">
              {litter.kittens.map((kitten) => (
                <Grid container size={{ xs: 12, md: 2 }} key={kitten.name}>
                  <Card sx={{ borderRadius: 4, boxShadow: 3 }}>
                    <CardMedia
                      component="img"
                      image={kitten.images[0]}
                      alt={kitten.name}
                    />
                    <CardContent>
                      <Box textAlign={"left"}>
                        <Typography variant="h5">
                          {capitalizeFirstLetter(kitten.name)} 
                          { 
                            kitten.reserved 
                            ? <Button size="small" variant="contained" color="error" disabled sx={{ float: 'right' }}>{t("status.reserved")}</Button>
                            : <Button size="small" variant="outlined" color="success" onClick={() => handleNavigate(kitten.name)} sx={{ float: 'right' }}>{t("status.available")}</Button>
                          }
                        </Typography>
                        <Typography variant="body2" sx={{ my: 1 }}>
                          {kitten?.description}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {
                            kitten.personality.map(name => {
                              return (
                                <Chip key={name} label={name} size="small" />
                              )
                            }) 
                          }
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Paper>
      </AccordionDetails>
    </Accordion>
  )
}

export default KittenFamily;